/**
 * MatchEvaluationRowView — one fixture this player was evaluated in, as a row
 * of the player page's reports list (SE-7856).
 *
 * A molecule: atoms only.
 *
 * **It is a sibling of `ReportItem`, not a replacement.** A scout report opens
 * a full-screen modal because it carries a PDF, a Form.io editor, shares and
 * an archive action. A match evaluation carries a note and two scores, so it
 * expands in place — which is also what Figma frame 26 draws. Two disclosure
 * models in one list is a deliberate choice: each row behaves the way its own
 * design says, rather than one being bent to match the other.
 *
 * **The owner leads and later reads show by name.** Two scouts on one fixture
 * are two independent reads; the first to evaluate owns the written report and
 * each additional read renders as "also evaluated" with its own scores. There
 * is deliberately NO consolidated number — a merged figure would invent a
 * rating nobody gave.
 *
 * **Only this player's evaluation is ever rendered.** The server projects the
 * field to the queried player, so no other player's note is reachable here.
 * This view never receives one and must never learn how to show one.
 *
 * This mirrors `player-match-reports/…/PlayerReportRow` visually on purpose.
 * That component is not reused because it is typed to that feature's
 * `PlayerEvaluationEntry`, keyed on match-scoped test ids, and carries SE-6986
 * staleness this row has no data for. The duplication is a knowing trade.
 * @module features/reports/components/molecules/MatchEvaluationRow/MatchEvaluationRowView
 */
import { ChevronDown, ChevronRight } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * One scout's read, as the row prints it.
 *
 * Display primitives, not the GraphQL record. The Container resolves scores to
 * their printed form so this View never has to know that a score is nullable,
 * that null and 0 differ, or which fragment the row came from — the boundary
 * the repo's Container/View rule exists to hold.
 */
export interface EvaluationLine {
  /** React key — the evaluation's own id. */
  readonly id: string;
  /** Who wrote it. */
  readonly scoutName: string;
  /** Performance, already printed — a number or an em dash. */
  readonly performanceLabel: string;
  /** Potential, already printed. */
  readonly potentialLabel: string;
  /** The scout's note, or null when they wrote none. */
  readonly note: string | null;
}

/** Props for MatchEvaluationRowView. */
export interface MatchEvaluationRowViewProps {
  /** "Sporting CP vs Benfica". */
  readonly title: string;
  /**
   * The meta line's parts, in order — kind first, then date, then scout.
   *
   * Joined here rather than by the caller so every row in this list separates
   * its parts the same way.
   */
  readonly metaParts: readonly string[];
  /** The first scout to evaluate this player in this fixture. */
  readonly owner: EvaluationLine;
  /** Every later read of the same player in the same fixture. */
  readonly others: readonly EvaluationLine[];
  /**
   * Whether the owning read carries neither score.
   *
   * Decided by the Container, and rendered as the words "Not scored" rather
   * than two em dashes — which read as data that failed to load rather than as
   * the scouting fact they are: a scout attended to this player and never
   * rated them.
   */
  readonly isUnscored: boolean;
  /** Whether the evaluation body is showing. */
  readonly isExpanded: boolean;
  /** Expands or collapses the row. */
  readonly onToggle: () => void;
  /** Opens the full match report. */
  readonly onOpenReport: () => void;
  /** Test identity. */
  readonly testID?: string;
  /** Test identity for the labelled Perf/Pot pair. */
  readonly scoresTestID?: string;
}

/**
 * Renders one match-report fixture row in the player page reports list.
 * @param root0 - Component props
 * @param root0.title - Fixture title
 * @param root0.metaParts - Meta line parts, joined with a bullet
 * @param root0.owner - The owning read
 * @param root0.others - The additional reads
 * @param root0.isUnscored - Whether the owning read carries neither score
 * @param root0.isExpanded - Whether the evaluation is showing
 * @param root0.onToggle - Expands or collapses
 * @param root0.onOpenReport - Opens the full match report
 * @param root0.testID - Test identity
 * @param root0.scoresTestID - Test identity for the score pair
 * @returns The row element.
 */
const MatchEvaluationRowView = ({
  title,
  metaParts,
  owner,
  others,
  isUnscored,
  isExpanded,
  onToggle,
  onOpenReport,
  testID,
  scoresTestID,
}: MatchEvaluationRowViewProps) => (
  <Box
    testID={testID}
    className="overflow-hidden rounded-lg border border-outline-default bg-surface-raised"
  >
    <Pressable
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityState={{ expanded: isExpanded }}
      accessibilityLabel={`${title}, ${metaParts.join(", ")}`}
      className="web:cursor-pointer web:hover:bg-surface-subtle flex-row items-center gap-3 px-4 py-3"
    >
      <Icon
        as={isExpanded ? ChevronDown : ChevronRight}
        size="sm"
        className="text-content-secondary"
      />

      <Stack space="micro" className="min-w-40 flex-1">
        <Text variant="body-strong" className="text-content-primary">
          {title}
        </Text>
        <Text variant="caption" className="text-content-secondary">
          {metaParts.join(" • ")}
        </Text>
      </Stack>

      {/* A fixed width, so the pair sits on the same vertical line down the
          list whether it reads "8 · 9", "8 · —" or "Not scored" — a column
          that shifts per row is read as noise rather than as a column. */}
      <Stack space="micro" testID={scoresTestID} className="w-16 items-end">
        {isUnscored ? (
          <Text variant="micro" className="text-content-subtle">
            Not scored
          </Text>
        ) : (
          <>
            <Text variant="micro" className="text-content-subtle">
              Perf · Pot
            </Text>
            <Text variant="caption-strong" className="text-content-primary">
              {owner.performanceLabel} · {owner.potentialLabel}
            </Text>
          </>
        )}
      </Stack>
    </Pressable>

    {isExpanded ? (
      <Stack space="3" className="border-t border-outline-subtle p-4">
        <Stack space="micro">
          <Text variant="caption-strong" className="text-content-primary">
            {owner.scoutName}
          </Text>
          <Text variant="caption" className="text-content-secondary">
            Performance {owner.performanceLabel} • Potential{" "}
            {owner.potentialLabel}
          </Text>
          {owner.note === null ? null : (
            <Text variant="caption" className="text-content-secondary">
              {owner.note}
            </Text>
          )}
        </Stack>

        {others.map(entry => (
          <Stack key={entry.id} space="micro">
            <Text variant="caption-strong" className="text-content-primary">
              {entry.scoutName} also evaluated this player
            </Text>
            <Text variant="caption" className="text-content-secondary">
              Performance {entry.performanceLabel} • Potential{" "}
              {entry.potentialLabel}
            </Text>
            {entry.note === null ? null : (
              <Text variant="caption" className="text-content-secondary">
                {entry.note}
              </Text>
            )}
          </Stack>
        ))}

        <Pressable
          onPress={onOpenReport}
          accessibilityRole="button"
          accessibilityLabel={`Open the full match report for ${title}`}
          className="web:cursor-pointer self-start"
        >
          <Text variant="caption-strong" className="text-accent-primary">
            Open full match report
          </Text>
        </Pressable>
      </Stack>
    ) : null}
  </Box>
);

MatchEvaluationRowView.displayName = "MatchEvaluationRowView";

export default memo(MatchEvaluationRowView);
